import React, { useState } from "react";
import { Mail, MapPin, Users, Clock, RefreshCw, ChevronDown } from "lucide-react";
import { UserAlertSubscription, SubscriptionInput, RuleType, AlertFrequency } from "../api/api";
import { actsAsKam } from "../lib/roles";
import { Switch } from "./ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuSeparator,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "./ui/dropdown-menu";

export const RULE_LABELS: Record<RuleType, string> = {
  new_vacancy:      "Nieuwe vacature",
  vacancy_update:   "Vacature update",
  stale_vacancy:    "Niet bijgewerkt",
  approval_reminder: "Herinnering mails ter goedkeuring",
};

export const RULE_DESC: Record<RuleType, string> = {
  new_vacancy:       "Ontvang een melding wanneer er een nieuwe vacature wordt aangemaakt.",
  vacancy_update:    "Ontvang een melding wanneer een vacature wordt gewijzigd.",
  stale_vacancy:     "Ontvang een melding wanneer een vacature gedurende een ingesteld aantal dagen niet is bijgewerkt.",
  approval_reminder: "Ontvang een herinnering zolang er e-mails wachten op uw goedkeuring.",
};

export const FREQ_LABELS: Record<AlertFrequency, string> = {
  immediate: "Direct",
  hourly:    "Uurlijks",
  daily:     "Dagelijks",
  weekly:    "Wekelijks",
  monthly:   "Maandelijks",
};

export const RULE_TYPES: RuleType[] = ["new_vacancy", "vacancy_update", "stale_vacancy", "approval_reminder"];

// Mirrors backend/internal/subscriptionconfig/registry.go — which roles may
// subscribe to which rule type.
export const RULE_ROLES: Record<RuleType, string[]> = {
  new_vacancy:        ["hmp", "partner_admin", "kam", "admin"],
  vacancy_update:     ["hmp", "partner_admin", "kam", "admin"],
  stale_vacancy:      ["hmp", "partner_admin", "kam", "admin"],
  approval_reminder:  ["admin", "kam"],
};

// Mirrors backend policy.CanSubscribeToRuleType: a rule open to "kam" is also
// open to an admin acting as a KAM (can_act_as_kam).
export const canSubscribeToRuleType = (u: { role: string; can_act_as_kam?: boolean }, ruleType: RuleType): boolean =>
  RULE_ROLES[ruleType].includes(u.role) || (actsAsKam(u) && RULE_ROLES[ruleType].includes("kam"));

// Mirrors backend/internal/subscriptionconfig/registry.go — which
// frequencies are valid for which rule type.
export const RULE_FREQUENCIES: Record<RuleType, AlertFrequency[]> = {
  new_vacancy:        ["immediate", "hourly", "daily", "weekly", "monthly"],
  vacancy_update:     ["immediate", "hourly", "daily", "weekly", "monthly"],
  stale_vacancy:      ["hourly", "daily", "weekly", "monthly"],
  approval_reminder:  ["immediate", "hourly", "daily"],
};

// Mirrors the defaults in backend/internal/subscriptionconfig/schedule.go.
export const DEFAULT_SEND_HOUR = 8;
export const DEFAULT_SEND_MINUTE = 0;
export const DEFAULT_SEND_WEEKDAY = 1;

// ISO weekday (1 = maandag … 7 = zondag), matching the backend.
export const WEEKDAY_LABELS: Record<number, string> = {
  1: "Maandag", 2: "Dinsdag", 3: "Woensdag", 4: "Donderdag",
  5: "Vrijdag", 6: "Zaterdag", 7: "Zondag",
};

// Every quarter hour of the day, as [hour, minute].
export const SEND_TIMES: [number, number][] = Array.from({ length: 96 }, (_, i) => [Math.floor(i / 4), (i % 4) * 15]);

export function formatSendTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function hasSendSlot(frequency: AlertFrequency): boolean {
  return frequency === "daily" || frequency === "weekly";
}

// approval_reminder isn't about a vacature, so it has no vestiging filter
// (the backend rejects branch_ids on it).
export function hasVestigingFilter(ruleType: RuleType): boolean {
  return ruleType !== "approval_reminder";
}

const DEFAULT_FREQUENCY: Record<RuleType, AlertFrequency> = {
  new_vacancy:        "immediate",
  vacancy_update:     "immediate",
  stale_vacancy:      "daily",
  approval_reminder:  "immediate",
};

export function defaultInput(ruleType: RuleType): SubscriptionInput {
  return {
    enabled:    true,
    frequency:  DEFAULT_FREQUENCY[ruleType],
    config:     ruleType === "stale_vacancy" ? { stale_days: 14 } : {},
    send_email: true,
  };
}

export interface VestigingOption {
  id:    number;
  label: string;
}

// Builds the vestiging filter options from branches, labelled by vestigingsnaam.
// A name shared by branches of different partners gets the partner appended.
export function buildVestigingOptions(
  branches: { id: number; name: string; partner_id: number }[],
  partners: { id: number; name: string }[],
): VestigingOption[] {
  const partnerName = new Map(partners.map((p) => [p.id, p.name]));
  const nameCount = new Map<string, number>();
  for (const b of branches) nameCount.set(b.name, (nameCount.get(b.name) ?? 0) + 1);
  return branches
    .map((b) => ({
      id: b.id,
      label: (nameCount.get(b.name) ?? 0) > 1
        ? `${b.name} · ${partnerName.get(b.partner_id) ?? `Partner #${b.partner_id}`}`
        : b.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface RuleCardProps {
  ruleType:         RuleType;
  sub?:             UserAlertSubscription;
  isKam:            boolean;
  partners:         { id: number; name: string }[];
  vestigingOptions: VestigingOption[];
  onToggle:         (enabled: boolean) => void;
  onSave:           (body: SubscriptionInput) => void;
  saving:           boolean;
}

export function RuleCard({ ruleType, sub, isKam, partners, vestigingOptions, onToggle, onSave, saving }: RuleCardProps) {
  const enabled = sub?.enabled ?? false;
  const [expanded, setExpanded] = useState(false);
  const [frequency, setFrequency] = useState<AlertFrequency>(
    sub?.frequency ?? DEFAULT_FREQUENCY[ruleType]
  );
  const [sendEmail, setSendEmail] = useState<boolean>(sub?.send_email ?? true);
  const [sendHour, setSendHour] = useState<number>(sub?.send_hour ?? DEFAULT_SEND_HOUR);
  const [sendMinute, setSendMinute] = useState<number>(sub?.send_minute ?? DEFAULT_SEND_MINUTE);
  const [sendWeekday, setSendWeekday] = useState<number>(sub?.send_weekday ?? DEFAULT_SEND_WEEKDAY);
  const [staleDays, setStaleDays] = useState<number>(sub?.config?.stale_days ?? 14);
  const [ownOnly, setOwnOnly] = useState<boolean>(sub?.config?.own_only ?? false);
  const [vestigingInput, setVestigingInput] = useState<number[]>(
    sub?.config?.branch_ids ?? []
  );
  const [partnerInput, setPartnerInput] = useState<number[]>(
    sub?.config?.partner_ids ?? []
  );

  function buildInput(): SubscriptionInput {
    // Selecting every known vestiging means "all of them", including ones added
    // later — so store that as an empty filter rather than the current, stale list.
    const selectedAll = vestigingOptions.length > 0
      && vestigingOptions.every((o) => vestigingInput.includes(o.id));
    const branchIds = selectedAll ? [] : vestigingInput;
    const config: SubscriptionInput["config"] = {};
    if (hasVestigingFilter(ruleType) && branchIds.length) config.branch_ids = branchIds;
    if (ruleType === "stale_vacancy") config.stale_days = staleDays;
    if (ruleType === "vacancy_update" && ownOnly) config.own_only = true;
    if (isKam && partnerInput.length) config.partner_ids = partnerInput;
    return {
      enabled: sub?.enabled ?? true, frequency, config, send_email: sendEmail,
      send_hour: sendHour, send_minute: sendMinute, send_weekday: sendWeekday,
    };
  }

  function handleSave() {
    onSave(buildInput());
    setExpanded(false);
  }

  return (
    <div className={`overflow-hidden rounded-[16px] border border-waves-line ${enabled ? "bg-white" : "bg-waves-subtle"}`}>
      <div className="flex items-center justify-between gap-4 px-5 py-[18px]">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <div className={`text-[15px] font-bold ${enabled ? "text-waves-ink" : "text-waves-muted"}`}>
              {RULE_LABELS[ruleType]}
            </div>
            <div className="text-sm text-waves-body">{RULE_DESC[ruleType]}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {enabled && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-[13px] font-bold text-waves-ink hover:text-waves-accent"
            >
              {expanded ? "Sluiten" : "Geavanceerd"}
            </button>
          )}
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            disabled={saving}
          />
        </div>
      </div>

      {enabled && !expanded && sub && (
        <div className="flex flex-wrap gap-2 px-5 pb-4">
          <Chip icon={<Mail size={10} />} label={FREQ_LABELS[sub.frequency]} />
          {sub.send_email && hasSendSlot(sub.frequency) && (
            <Chip
              icon={<Clock size={10} />}
              label={sub.frequency === "weekly"
                ? `${WEEKDAY_LABELS[sub.send_weekday]} ${formatSendTime(sub.send_hour, sub.send_minute)}`
                : formatSendTime(sub.send_hour, sub.send_minute)}
            />
          )}
          {!sub.send_email && <Chip icon={<Mail size={10} />} label="Geen e-mail" muted />}
          {hasVestigingFilter(ruleType) && (sub.config.branch_ids?.length
            ? <Chip icon={<MapPin size={10} />} label={sub.config.branch_ids
                .map((id) => vestigingOptions.find((o) => o.id === id)?.label ?? `Vestiging #${id}`)
                .join(", ")} />
            : <Chip icon={<MapPin size={10} />} label="Alle vestigingen" muted />)}
          {ruleType === "stale_vacancy" && (
            <Chip icon={<Clock size={10} />} label={`${sub.config.stale_days ?? 14}d`} />
          )}
          {ruleType === "vacancy_update" && sub.config.own_only && (
            <Chip icon={<RefreshCw size={10} />} label="Alleen mijn vacatures" />
          )}
          {isKam && sub.config.partner_ids?.length
            ? <Chip icon={<Users size={10} />} label={`${sub.config.partner_ids.length} partner(s)`} />
            : isKam ? <Chip icon={<Users size={10} />} label="Alle partners" muted /> : null}
        </div>
      )}

      {expanded && (
        <div className="flex flex-col gap-4 border-t border-waves-line bg-waves-subtle px-5 py-5">

          {/* Frequency */}
          <div>
            <label className="mb-2 block text-[13px] font-bold text-waves-ink">Frequentie</label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-11 w-full items-center justify-between rounded-nav border border-waves-line bg-white px-4 text-[15px] outline-none focus:border-waves-zee"
                >
                  <span className="text-waves-ink">{FREQ_LABELS[frequency]}</span>
                  <ChevronDown size={14} className="text-waves-muted" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                <DropdownMenuRadioGroup value={frequency} onValueChange={(v) => setFrequency(v as AlertFrequency)}>
                  {RULE_FREQUENCIES[ruleType].map((v) => (
                    <DropdownMenuRadioItem key={v} value={v}>{FREQ_LABELS[v]}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Send email */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] font-bold text-waves-ink">E-mail versturen</div>
              <div className="text-xs text-waves-muted">Ontvang deze melding ook per e-mail, naast de melding in de app.</div>
            </div>
            <Switch checked={sendEmail} onCheckedChange={setSendEmail} />
          </div>

          {/* Send slot (daily/weekly e-mail only) */}
          {sendEmail && hasSendSlot(frequency) && (
            <div>
              <div className="flex gap-3">
                {frequency === "weekly" && (
                  <div className="flex-1">
                    <label className="mb-2 block text-[13px] font-bold text-waves-ink">Verzenddag</label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex h-11 w-full items-center justify-between rounded-nav border border-waves-line bg-white px-4 text-[15px] outline-none focus:border-waves-zee"
                        >
                          <span className="text-waves-ink">{WEEKDAY_LABELS[sendWeekday]}</span>
                          <ChevronDown size={14} className="text-waves-muted" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                        <DropdownMenuRadioGroup value={String(sendWeekday)} onValueChange={(v) => setSendWeekday(Number(v))}>
                          {Object.entries(WEEKDAY_LABELS).map(([v, label]) => (
                            <DropdownMenuRadioItem key={v} value={v}>{label}</DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                <div className="flex-1">
                  <label className="mb-2 block text-[13px] font-bold text-waves-ink">Verzendtijd</label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-11 w-full items-center justify-between rounded-nav border border-waves-line bg-white px-4 text-[15px] outline-none focus:border-waves-zee"
                      >
                        <span className="font-mono text-waves-ink">{formatSendTime(sendHour, sendMinute)}</span>
                        <ChevronDown size={14} className="text-waves-muted" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                      <DropdownMenuRadioGroup
                        value={formatSendTime(sendHour, sendMinute)}
                        onValueChange={(v) => {
                          const [h, m] = v.split(":").map(Number);
                          setSendHour(h);
                          setSendMinute(m);
                        }}
                      >
                        {SEND_TIMES.map(([h, m]) => (
                          <DropdownMenuRadioItem key={formatSendTime(h, m)} value={formatSendTime(h, m)} className="font-mono">
                            {formatSendTime(h, m)}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <p className="text-xs text-waves-muted mt-1">
                Nederlandse tijd. Geldt alleen voor de e-mail; meldingen in de app komen direct binnen. De e-mail kan enkele minuten later aankomen.
              </p>
            </div>
          )}

          {/* Stale days */}
          {ruleType === "stale_vacancy" && (
            <div>
              <label className="mb-2 block text-[13px] font-bold text-waves-ink">
                Dagen zonder update
              </label>
              <input
                type="number"
                min={1}
                value={staleDays}
                onChange={(e) => setStaleDays(parseInt(e.target.value) || 14)}
                className="h-11 w-full rounded-nav border border-waves-line bg-white px-4 text-[15px] outline-none focus:border-waves-zee focus:ring-2 focus:ring-waves-zee/25"
              />
            </div>
          )}

          {/* Vestiging */}
          {hasVestigingFilter(ruleType) && (
            <div>
              <label className="mb-2 block text-[13px] font-bold text-waves-ink">
                Vestigingen
              </label>
              <VestigingMultiSelect
                options={vestigingOptions}
                selected={vestigingInput}
                onChange={setVestigingInput}
              />
              <p className="text-xs text-waves-muted mt-1">
                Alles selecteren telt als alle vestigingen — nieuwe vestigingen worden dan automatisch meegenomen.
              </p>
            </div>
          )}

          {/* Own only (vacancy_update) */}
          {ruleType === "vacancy_update" && (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[13px] font-bold text-waves-ink">Alleen mijn vacatures</div>
                <div className="text-xs text-waves-muted">Alleen meldingen voor vacatures die u aangemaakt of gewijzigd heeft.</div>
              </div>
              <Switch checked={ownOnly} onCheckedChange={setOwnOnly} />
            </div>
          )}

          {/* Partner filter (KAM only) */}
          {isKam && partners.length > 0 && (
            <div>
              <label className="mb-2 block text-[13px] font-bold text-waves-ink">
                Partners (leeg = alle partners)
              </label>
              <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                {partners.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm text-waves-ink">
                    <input
                      type="checkbox"
                      checked={partnerInput.includes(p.id)}
                      onChange={(e) =>
                        setPartnerInput(
                          e.target.checked
                            ? [...partnerInput, p.id]
                            : partnerInput.filter((id) => id !== p.id)
                        )
                      }
                      className="accent-waves-ink"
                    />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => setExpanded(false)}
              className="inline-flex h-9 items-center rounded-pill border border-waves-line bg-white px-4 text-[13px] font-bold text-waves-ink hover:bg-waves-mist"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex h-9 items-center rounded-pill bg-waves-ink px-4 text-[13px] font-bold text-white hover:bg-waves-ink-deep disabled:opacity-60"
            >
              Opslaan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface VestigingMultiSelectProps {
  options:  VestigingOption[];
  selected: number[];
  onChange: (next: number[]) => void;
}

export function VestigingMultiSelect({ options, selected, onChange }: VestigingMultiSelectProps) {
  const allSelected = options.length > 0 && options.every((o) => selected.includes(o.id));

  function toggle(id: number) {
    onChange(
      selected.includes(id)
        ? selected.filter((o) => o !== id)
        : [...selected, id]
    );
  }

  const label = selected.length === 0
    ? "Alle vestigingen"
    : allSelected
      ? "Alle vestigingen"
      : `${selected.length} vestiging${selected.length === 1 ? "" : "en"} geselecteerd`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={options.length === 0}
          className="flex h-11 w-full items-center justify-between rounded-nav border border-waves-line bg-white px-4 text-[15px] outline-none focus:border-waves-zee disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className={selected.length === 0 ? "text-waves-muted" : "text-waves-ink"}>
            {options.length === 0 ? "Geen vestigingen beschikbaar" : label}
          </span>
          <ChevronDown size={14} className="text-waves-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
        <DropdownMenuCheckboxItem
          checked={allSelected}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={() => onChange(allSelected ? [] : options.map((o) => o.id))}
        >
          Alle vestigingen
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.id}
            checked={selected.includes(option.id)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(option.id)}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Chip({ icon, label, muted }: { icon: React.ReactNode; label: string; muted?: boolean }) {
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
      muted ? "bg-status-neutral-bg text-waves-muted" : "bg-waves-mist text-waves-ink"
    }`}>
      {icon} {label}
    </span>
  );
}
